import { CornerDownLeft } from "lucide-react";

import { cn } from "@/lib/utils";

interface ClarificationChoicesProps {
  /**
   * Complete, ready-to-send requests — the original sentence with the
   * disambiguating words added. Clicking one sends it verbatim.
   */
  options: string[];
  onChoose: (option: string) => void;
  isBusy: boolean;
}

/** One choice, as it is shown: a short label and the sentence it will send. */
interface Choice {
  /** The part of the sentence that differs from the other options. */
  label: string;
  /** What actually gets sent. Identical to `label` when nothing could be derived. */
  request: string;
}

/**
 * The answer to a question the composer just asked.
 *
 * A clarification turn carries two or more complete requests — the requestor's
 * own sentence, each time with the words that would have settled it. Clicking
 * one sends it exactly as if it had been typed, so the thread records a request
 * the requestor can read back rather than a hidden "option B".
 *
 * These deliberately do not look like the opening `EXAMPLES` chips. Those are
 * half-written openings that drop into the box for someone to finish; these are
 * whole sentences that are sent on click. Same surface, opposite consequence —
 * so they read as a decision, with the sentence visible before it goes.
 */
export function ClarificationChoices({ options, onChoose, isBusy }: ClarificationChoicesProps) {
  if (options.length === 0) {
    return null;
  }

  const choices = asChoices(options);

  return (
    <section className="border-border bg-surface-muted/60 space-y-3 rounded-lg border p-4">
      <header className="space-y-1">
        <h3 className="font-heading text-foreground text-sm font-bold">Which did you mean?</h3>
        {/* Count-aware, because a third option would make "both" a small lie
            on a card whose whole job is not to guess. */}
        <p className="text-muted text-xs leading-5">
          {options.length === 2 ? "Both readings fit" : "Each of these fits"} what you asked for.
          Pick one and I&apos;ll send it as your request — or ignore these and say it your own way
          below.
        </p>
      </header>

      {/* One per row at every width: these are sentences, not chips, and a
          wrapped sentence beside another wrapped sentence is unreadable. */}
      <div className="flex flex-col gap-2">
        {choices.map((choice, index) => (
          <button
            // By position: nothing stops a turn from offering the same sentence
            // twice, and the list never reorders within a turn.
            key={`${index}-${choice.request}`}
            type="button"
            disabled={isBusy}
            onClick={() => onChoose(choice.request)}
            className={cn(
              "border-border bg-card hover:border-velux/40 focus-visible:ring-velux/20",
              "group flex w-full items-start gap-3 rounded-lg border p-3 text-left transition",
              "focus-visible:ring-2 focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            <CornerDownLeft className="text-muted group-hover:text-velux mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 space-y-1">
              <span className="text-foreground block text-sm font-semibold break-words">
                {choice.label}
              </span>
              {/* The whole sentence, always — the short label is a summary, and
                  a button that sends ninety characters should not hide them. */}
              {choice.label !== choice.request && (
                <span className="text-muted block text-xs leading-5 break-words">
                  <span className="type-label text-muted/80 mr-1.5">Sends</span>
                  {choice.request}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Longest word-level prefix the options agree on, compared case-insensitively. */
function commonPrefix(words: string[][]): number {
  const first = words[0];
  if (!first) {
    return 0;
  }
  const shortest = Math.min(...words.map((item) => item.length));
  let count = 0;
  while (count < shortest && words.every((item) => same(item[count], first[count]))) {
    count += 1;
  }
  return count;
}

function same(a: string | undefined, b: string | undefined): boolean {
  return (a ?? "").toLowerCase() === (b ?? "").toLowerCase();
}

/** Drop the separators a tail clause starts or ends with, and open with a capital. */
function tidy(text: string): string {
  const trimmed = text.replace(/^[\s—–\-,;:]+/, "").replace(/[\s—–\-,;:]+$/, "");
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Give each option a short label by keeping only what makes it different.
 *
 * The options are one sentence written several ways, so the words they share
 * carry no information: strip the matching head and tail and what is left is
 * precisely the choice being made — "just the text, no image" against "build
 * the ad with an approved photograph". Derived here rather than asked of the
 * backend, which sends one field and should keep sending one field.
 *
 * Anything that comes out empty, too long to read as a label, or too short to
 * mean anything sends every option back to its full sentence. A wrong label is
 * worse than a long one, because the sentence is what gets sent.
 */
function asChoices(options: string[]): Choice[] {
  const full = options.map((request) => ({ label: request, request }));
  if (options.length < 2) {
    return full;
  }

  const words = options.map((option) => option.split(/\s+/).filter(Boolean));
  const head = commonPrefix(words);
  const reversed = words.map((item) => [...item].reverse());
  // Never let the head and the tail overlap on the shortest option, or the
  // middle would be read out of a range that has already been consumed.
  const tail = Math.min(
    commonPrefix(reversed),
    ...words.map((item) => Math.max(0, item.length - head)),
  );
  if (head === 0 && tail === 0) {
    return full;
  }

  const labels = words.map((item) => tidy(item.slice(head, item.length - tail).join(" ")));
  const usable =
    labels.every((label) => label.length >= 3 && label.length <= 70) &&
    new Set(labels.map((label) => label.toLowerCase())).size === labels.length;
  return usable
    ? options.map((request, index) => ({ label: labels[index] ?? request, request }))
    : full;
}
